import React from "react";

/**
 * PasswordStrength — 4-tier live strength meter.
 *
 * Tiers (based on a small heuristic — length + character variety + common-pattern penalty):
 *   0  too weak      red
 *   1  fair          amber
 *   2  strong        yellow
 *   3  excellent     green
 *
 * Not a substitute for server-side length enforcement, but nudges users to pick
 * a password that survives a targeted guess.
 */
const COMMON = [
  "password", "12345", "qwerty", "letmein", "welcome", "abc123", "iloveyou",
  "admin", "monkey", "dragon", "draftwave",
];

export function scorePassword(pw) {
  if (!pw) return { score: 0, tier: 0, hint: "Add at least 8 characters." };
  const len = pw.length;
  let s = 0;
  if (len >= 8)  s += 1;
  if (len >= 12) s += 1;
  if (len >= 16) s += 1;
  const lower = /[a-z]/.test(pw);
  const upper = /[A-Z]/.test(pw);
  const digit = /\d/.test(pw);
  const sym   = /[^A-Za-z0-9]/.test(pw);
  s += [lower, upper, digit, sym].filter(Boolean).length; // 0..4
  // Common-password penalty
  const lc = pw.toLowerCase();
  if (COMMON.some((c) => lc.includes(c))) s = Math.max(0, s - 3);
  // Cap
  s = Math.max(0, Math.min(s, 7));

  let tier = 0;
  if (s >= 6 && len >= 12) tier = 3;
  else if (s >= 5 && len >= 10) tier = 2;
  else if (s >= 3 && len >= 8)  tier = 1;
  else tier = 0;

  let hint = "";
  if (tier === 0) {
    if (len < 8) hint = "Add at least 8 characters.";
    else hint = "Mix upper, lower, number and a symbol.";
  } else if (tier === 1) {
    if (!upper) hint = "Add an UPPERCASE letter for extra strength.";
    else if (!digit) hint = "Add a number for extra strength.";
    else if (!sym) hint = "Add a symbol like ! or # for extra strength.";
    else hint = "Try making it a few characters longer.";
  } else if (tier === 2) {
    hint = "Great. A bit longer would make it excellent.";
  } else {
    hint = "Excellent — this is production-grade.";
  }
  return { score: s, tier, hint };
}

const TIER_META = [
  { label: "Too weak",  bg: "#DC2626", text: "#DC2626" }, // red-600
  { label: "Fair",      bg: "#F59E0B", text: "#B45309" }, // amber-500 / amber-700
  { label: "Strong",    bg: "#EAB308", text: "#A16207" }, // yellow-500 / yellow-700
  { label: "Excellent", bg: "#10B981", text: "#047857" }, // emerald-500 / emerald-700
];

export default function PasswordStrength({ password }) {
  const { tier, hint } = scorePassword(password);
  const meta = TIER_META[tier];
  const filled = tier + 1; // 1..4
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="h-1 flex-1 transition-colors"
            style={{ backgroundColor: i < filled ? meta.bg : "hsl(var(--border))" }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
        <span style={{ color: meta.text }} data-testid="password-strength-label">
          {meta.label}
        </span>
        <span className="text-muted-foreground text-right ml-3 normal-case tracking-normal font-sans text-[11px]" data-testid="password-strength-hint">
          {hint}
        </span>
      </div>
    </div>
  );
}
